import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { IoChatboxOutline, IoBanOutline } from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { getComplaintsByPartner, updateComplaint } from '../../../services/complaintService';

const STATUS_LABEL = {
  pending: '대기',
  in_review: '검토중',
  resolved: '처리완료',
  rejected: '반려',
};

const STATUS_COLOR = {
  pending: { bg: '#FEF3C7', fg: '#D97706' },
  in_review: { bg: '#DBEAFE', fg: '#2563EB' },
  resolved: { bg: '#D1FAE5', fg: '#059669' },
  rejected: { bg: '#E5E7EB', fg: '#6B7280' },
};

const ComplaintHandle = () => {
  const { userData } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!userData?.uid) return;
    setLoading(true);
    try {
      const items = await getComplaintsByPartner(userData.uid);
      setList(items);
    } catch (err) {
      console.error('complaints load failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userData?.uid]);

  const handleResolve = async (id) => {
    try {
      await updateComplaint(id, { status: 'resolved', followUpBy: userData?.uid });
      load();
    } catch (err) {
      console.error('resolve failed', err);
      alert('처리 실패');
    }
  };

  return (
    <Wrap>
      <PageTitle>고객 민원 관리</PageTitle>
      <PageDesc>배정된 고객의 민원 내역을 확인하고 처리하는 공간입니다.</PageDesc>

      <Card>
        <CardHeader>
          <IoChatboxOutline size={18} />
          <CardTitle>민원 목록 ({list.length}건)</CardTitle>
        </CardHeader>

        {loading ? (
          <EmptyBox><EmptyText>불러오는 중...</EmptyText></EmptyBox>
        ) : list.length === 0 ? (
          <EmptyBox>
            <IoBanOutline size={48} />
            <EmptyText>배정된 민원이 없습니다.</EmptyText>
          </EmptyBox>
        ) : (
          <List>
            {list.map((c) => {
              const st = STATUS_COLOR[c.status] || STATUS_COLOR.pending;
              return (
                <Item key={c.id}>
                  <Row>
                    <ItemTitle>{c.customerName || '-'} · {c.type || '민원'}</ItemTitle>
                    <Badge $bg={st.bg} $fg={st.fg}>{STATUS_LABEL[c.status] || c.status}</Badge>
                  </Row>
                  <ItemContent>{c.summary || c.content || '-'}</ItemContent>
                  <Row>
                    <Date>{c.date}</Date>
                    {c.status !== 'resolved' && (
                      <ActionBtn type="button" onClick={() => handleResolve(c.id)}>처리 완료</ActionBtn>
                    )}
                  </Row>
                </Item>
              );
            })}
          </List>
        )}
      </Card>
    </Wrap>
  );
};

export default ComplaintHandle;

// ───── 스타일 ─────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    max-width: 720px;
    margin: 0 auto;
    width: 100%;
  }
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs};

  @media (min-width: 1024px) {
    font-size: 22px;
    text-align: center;
    margin-bottom: 8px;
  }
`;

const PageDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  line-height: 1.6;

  @media (min-width: 1024px) {
    text-align: center;
    margin-bottom: 20px;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.base};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.text};
`;

const CardTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin: 0;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xxxl} ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textLight};
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Item = styled.li`
  padding: ${({ theme }) => theme.spacing.base};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};

  &:last-child { border-bottom: 0; }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ItemTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const ItemContent = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const Badge = styled.span`
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const Date = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ActionBtn = styled.button`
  padding: 6px 12px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
`;
